import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { buildPageDocumentTitle } from '@/components/Layout/AppShell/documentTitle';
import { useDocumentTitleOverride } from '@/components/Layout/AppShell/documentTitleOverride';
import { EmptyState } from '@/components/molecules/EmptyState';

/**
 * What an unknown address shows: a path no route has, or an id (client, site, pen, bioscope)
 * that does not exist or that the user cannot open. The two read the same, so a page does not
 * tell a user which ids exist elsewhere. The URL stays as typed.
 */
interface NotFoundProps {
    /**
     * The page title in place of the page, so `h1` by default; `h2` inside a page that keeps
     * its own heading.
     */
    headingLevel?: 'h1' | 'h2';
}

export function NotFound(props: NotFoundProps) {
    const { headingLevel = 'h1' } = props;
    const { t } = useTranslation();
    useDocumentTitleOverride(buildPageDocumentTitle(t('common.notFound.title')));

    return (
        <EmptyState
            size="spacious"
            live="polite"
            headingLevel={headingLevel}
            title={t('common.notFound.title')}
            description={t('common.notFound.description')}
            action={
                <Button variant="secondary" to="/">
                    {t('common.notFound.home')}
                </Button>
            }
        />
    );
}
